import { useState, FC } from 'react';
import { Box, IconButton, Tooltip, Popover, Typography, Button, Grid } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from 'react-i18next';
import TimeHelper from '../../../utils/time';

interface SCUIQuickDateSelectorProps {
    /**
     * Callback function called when a date range is selected
     * @param startDate - Start date in YYYY-MM-DD format
     * @param endDate - End date in YYYY-MM-DD format
     */
    onDateRangeSelect: (startDate: string, endDate: string) => void;
    /**
     * Translation namespace for date selection labels
     * Default: 'comments'
     */
    translationNamespace?: string;
    /**
     * Custom tooltip text
     */
    tooltipText?: string;
    /**
     * Icon button size
     */
    iconSize?: 'small' | 'medium' | 'large';
    /**
     * Icon button color
     */
    iconColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
}

const SCUIQuickDateSelector: FC<SCUIQuickDateSelectorProps> = ({
    onDateRangeSelect,
    translationNamespace = 'comments',
    tooltipText,
    iconSize = 'small',
    iconColor = 'default',
}) => {
    const { t } = useTranslation();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDateRangeSelect = (type: 'today' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth') => {
        const { startDate, endDate } = TimeHelper.getQuickDateRange(type);
        onDateRangeSelect(startDate, endDate);
        handleClose();
    };

    const tooltipTitle = tooltipText || t(`${translationNamespace}.quickDateSelectionHeader`);

    return (
        <>
            <Tooltip title={tooltipTitle}>
                <IconButton
                    size={iconSize}
                    color={iconColor}
                    onClick={handleOpen}
                    sx={{
                        borderRadius: '999px',
                        padding: '6px 10px',
                        border: '1px solid rgba(25, 118, 210, 0.4)',
                        background: 'linear-gradient(135deg, #f3f6ff 0%, #e0edff 100%)',
                        boxShadow: '0 2px 6px rgba(15, 23, 42, 0.18)',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                            background: 'linear-gradient(135deg, #e0edff 0%, #c7ddff 100%)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 10px rgba(15, 23, 42, 0.22)',
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                            boxShadow: '0 2px 6px rgba(15, 23, 42, 0.18)',
                        },
                    }}
                >
                    <CalendarTodayIcon
                        fontSize={iconSize}
                        sx={{
                            color: 'primary.main',
                        }}
                    />
                </IconButton>
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        elevation: 8,
                        sx: {
                            borderRadius: 2,
                            mt: 1,
                            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.28), 0 1px 4px rgba(15, 23, 42, 0.12)',
                            border: '1px solid rgba(148, 163, 184, 0.35)',
                            bgcolor: 'background.paper',
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        maxWidth: 200,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 0.5,
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        {t(`${translationNamespace}.quickDateSelectionHeader`)}
                    </Typography>
                    <Grid container spacing={2.5}>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => handleDateRangeSelect('today')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148, 163, 184, 0.7)',
                                    color: 'text.primary',
                                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(219, 234, 254, 0.9)',
                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
                                    },
                                }}
                            >
                                {t(`${translationNamespace}.quickDateSelectionToday`)}
                            </Button>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => handleDateRangeSelect('thisWeek')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148, 163, 184, 0.7)',
                                    color: 'text.primary',
                                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(219, 234, 254, 0.9)',
                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
                                    },
                                }}
                            >
                                {t(`${translationNamespace}.quickDateSelectionThisWeek`)}
                            </Button>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => handleDateRangeSelect('lastWeek')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148, 163, 184, 0.7)',
                                    color: 'text.primary',
                                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(219, 234, 254, 0.9)',
                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
                                    },
                                }}
                            >
                                {t(`${translationNamespace}.quickDateSelectionLastWeek`)}
                            </Button>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => handleDateRangeSelect('thisMonth')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148, 163, 184, 0.7)',
                                    color: 'text.primary',
                                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(219, 234, 254, 0.9)',
                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
                                    },
                                }}
                            >
                                {t(`${translationNamespace}.quickDateSelectionThisMonth`)}
                            </Button>
                        </Grid>
                        <Grid
                            size={{
                                xs: 12,
                            }}
                        >
                            <Button
                                fullWidth
                                size="small"
                                variant="outlined"
                                onClick={() => handleDateRangeSelect('lastMonth')}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    fontWeight: 600,
                                    borderColor: 'rgba(148, 163, 184, 0.7)',
                                    color: 'text.primary',
                                    backgroundColor: 'rgba(248, 250, 252, 0.9)',
                                    '&:hover': {
                                        borderColor: 'primary.main',
                                        backgroundColor: 'rgba(219, 234, 254, 0.9)',
                                        boxShadow: '0 2px 8px rgba(15, 23, 42, 0.18)',
                                    },
                                }}
                            >
                                {t(`${translationNamespace}.quickDateSelectionLastMonth`)}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Popover>
        </>
    );
};

export default SCUIQuickDateSelector;
